import { defineStore } from 'pinia'
import pickBy from 'lodash/pickBy'
import merge from 'lodash/merge'
import { useApi } from '~/composables/useApi'
import { useDestinationsStore } from '~/stores/destinations'

function state () {
  return {
    perPage: 20,

    venues: {
      page: 1,
      order: null,
      loading: false,
      total: 0,
      data: [],
      firstLoad: true,
    },

    totals: {
      loading: false,
      total: 0,
    },

    facets: [],

    ranges: {
      min_price: 0,
      max_price: 1000,
    },

    filters: {
      top_left: null,
      bottom_right: null,
      venue_type_id: null,
      neighborhood_ids: [],
      category_ids: [],
      amenity_ids: [],
      price_category_ids: [],
      check_in: null,
      check_out: null,
      num_guests: null,
      num_rooms: null,
      min_price: null,
      max_price: null,
    },
  }
}

function isRangesDefault (ranges) {
  const initState = state()
  return ranges.min_price === initState.ranges.min_price && ranges.max_price === initState.ranges.max_price
}

export const useVenuesStore = defineStore('venues', {
  state: () => merge({}, state()),

  actions: {
    async loadVenues (other = {}) {
      this.venues.loading = true
      const filters = pickBy(this.filters, v => v !== null)
      if (this.venues.order) { other.order_by = this.venues.order }
      try {
        const resp = await useApi('venues.json', {
          query: {
            destination_id: useDestinationsStore().destination.data.id,
            offset: (this.venues.page - 1) * this.perPage,
            limit: this.perPage,
            show_featured_extracts: true,
            show_photos: true,
            facets: true,
            ...filters,
            ...other,
          },
        })

        this.venues.data = resp.response.venues
        this.venues.total = resp.response.total_records
        this.totals.total = resp.response.total_records
        if (resp.facets) { this.facets = resp.facets }
        if (resp.ranges && isRangesDefault(this.ranges)) { this.ranges = resp.ranges }
        this.venues.firstLoad = false
      } finally {
        this.venues.loading = false
      }
    },

    async loadTotals ({ destinationId, ...other }) {
      this.totals.loading = true
      if (other.min_price === 0 && other.max_price === 1000) {
        delete other.min_price
        delete other.max_price
      }

      try {
        const resp = await useApi('venues.json', {
          query: {
            destination_id: destinationId,
            offset: 0,
            limit: 0,
            facets: true,
            ...other,
          },
        })
        this.totals.total = resp.response.total_records
        if (resp.facets) { this.facets = resp.facets }
        if (resp.ranges && isRangesDefault(this.ranges)) { this.ranges = resp.ranges }
      } finally {
        this.totals.loading = false
      }
    },

    updateFilters (filters) {
      this.filters = Object.assign({}, this.filters, filters)
    },

    clearFilters () {
      this.filters = Object.assign({}, state().filters)
    },
  },

  getters: {
    neighborhoods () {
      const data = this.facets.find(f => f.name === 'neighborhood_ids')
      return (data && data.rows) || []
    },

    categories () {
      const data = this.facets.find(f => f.name === 'category_ids')
      return (data && data.rows) || []
    },

    amenities () {
      const data = this.facets.find(f => f.name === 'amenity_ids')
      return (data && data.rows) || []
    },

    priceCategories () {
      const data = this.facets.find(f => f.name === 'price_category_id')
      return (data && data.rows) || []
    },

    prices () {
      const data = this.facets.find(f => f.name === 'price')
      return (data && data.rows) || []
    },
  },
})
